import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { MobileListItem, Variable, MOBILE_EVENT_CODE } from '@mobileTypes'
import { v4 as uuidv4 } from 'uuid'

import { EventFlowsEditor } from './EventFlowsEditor'
import { useMobileGlobalVariables } from '@mobileHooks'

interface ActionsConfigTabProps {
  /** Form field name for event flows (default: 'eventFlows'). */
  eventFlowsFieldName?: string
  /** Form field name for variables (default: 'variables'). */
  variablesFieldName?: string
  /** When true, use map-specific allowed event codes (e.g. in Mobile Map configurator). */
  isMapContext?: boolean
}

export const ActionsConfigTab = ({
  eventFlowsFieldName = 'eventFlows',
  variablesFieldName = 'variables',
  isMapContext: isMapContextProp = false,
}: ActionsConfigTabProps = {}) => {
  const { watch, setValue } = useFormContext<MobileListItem | any>()
  const eventFlows = watch(eventFlowsFieldName) || []

  const watchedStartScreen = watch('startScreen')
  const watchedScreenIds = watch('screenIds')
  const isMobileAppContext = Boolean(watchedStartScreen !== undefined || watchedScreenIds !== undefined)
  const isMapContext = isMapContextProp

  const formElements = watch('elements') || []
  const bottomElements = watch('bottomElements') || []
  const allFormElements = [...formElements, ...bottomElements]
  const drawerItems = watch('drawerItems') || []

  const allowedEventCodes: MOBILE_EVENT_CODE[] | undefined = isMapContext
    ? [
        MOBILE_EVENT_CODE.ON_INIT,
        MOBILE_EVENT_CODE.ON_UNMOUNT,
        MOBILE_EVENT_CODE.ON_VARIABLE_CHANGE,
      ]
    : isMobileAppContext
      ? [
          MOBILE_EVENT_CODE.ON_INIT,
          MOBILE_EVENT_CODE.ON_UNMOUNT,
          MOBILE_EVENT_CODE.ON_INTERVAL,
          MOBILE_EVENT_CODE.ON_VARIABLE_CHANGE,
          MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS,
        ]
      : undefined

  const variables = watch(variablesFieldName) || []
  const variableOptions = variables.map((v: any) => ({ internalId: v.internalId, objectCode: v.objectCode }))
  const { variables: globalVariables, createGlobalVariable } = useMobileGlobalVariables()
  const globalVariableOptions = globalVariables.map((v: any) => ({ internalId: v.internalId, objectCode: v.objectCode }))

  const handleCreateVariable = useCallback(
    (variableInternalId: string, isGlobal: boolean = false, valueType: string = 'string') => {
      if (!variableInternalId?.trim()) return
      const trimmed = variableInternalId.trim()
      const existingLocal = variables.find((v: Variable) => v.internalId === trimmed)
      const existingGlobal = globalVariables.find((v: Variable) => v.internalId === trimmed)
      if (existingLocal || existingGlobal) return

      const newVariable = {
        internalId: trimmed,
        variableType: 'single' as const,
        valueType: valueType || 'string',
        defaultValue: undefined,
      }

      if (isGlobal) {
        createGlobalVariable(newVariable)
      } else {
        const localVariable: Variable = { ...newVariable, id: uuidv4() }
        setValue(variablesFieldName, [...variables, localVariable], { shouldDirty: true })
      }
    },
    [variables, globalVariables, setValue, createGlobalVariable, variablesFieldName]
  )

  return (
    <EventFlowsEditor
      eventFlows={eventFlows}
      onEventFlowsChange={flows => setValue(eventFlowsFieldName, flows, { shouldDirty: true })}
      formElements={allFormElements}
      drawerItems={drawerItems}
      allowedEventCodes={allowedEventCodes}
      showAddListenerButton={true}
      variables={variableOptions}
      globalVariables={globalVariableOptions}
      onCreateVariable={handleCreateVariable}
    />
  )
}
